<script setup lang="ts">
//#region import
import { ref } from 'vue';
import { useApi } from '@/api';
import { useCopy } from '@/composables/useCopy';
import type { FormErrors, EmitsToListeners } from '@/types/core';
//#endregion

//#region types
interface  Emits {
  'close': [],
  'success': [],
  'cancel': []
};

export interface CreateTokenParams {
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const emit = defineEmits<Emits>();
//#endregion

//#region const
const serviceOptions = [
  {
    label: 'API',
    value: 'api',
  },
  {
    label: 'Events Gateway',
    value: 'events-gw',
  },
];
//#endregion

//#region composables
const api = useApi();
const { copy } = useCopy();
//#endregion

//#region state
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const errors = ref<FormErrors>({});

const tokenName = ref('');
const accessKey = ref('');
const secretKey = ref('');
const service = ref<'api' | 'events-gw'>('api');
const completeState = ref(false);
//#endregion

//#region methods
const open = () => {
  isVisible.value = true;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};

const handleSave = () => {
  api.postAccessToken({ name: tokenName.value, service: service.value }).then((res) => {
    emit('success');
    const { id, secret_key } = res.data.payload;
    accessKey.value = id;
    secretKey.value = secret_key || '';
    completeState.value = true;
  }).catch((e) => {
    errors.value = e?.data?.errors || {};
  });
};
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="create-token-popup"
    :title="$t('settings.tokens.create_token')"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <template v-if="completeState">
          <div class="t:space-y-8">
            <CardTitle
              :title="$t('settings.tokens.access_key')"
            >
              <UiButton
                icon="clone"
                no-border
                @click="copy(accessKey)"
              >
                {{ $t('reports.other.copy') }}
              </UiButton>
            </CardTitle>
            <div
              class="create-token-popup__key"
              @click="copy(accessKey)"
            >
              {{ accessKey }}
            </div>
          </div>
          <div class="t:space-y-8">
            <CardTitle
              :title="$t('settings.tokens.secret_key')"
            >
              <UiButton
                icon="clone"
                no-border
                @click="copy(secretKey)"
              >
                {{ $t('reports.other.copy') }}
              </UiButton>
            </CardTitle>
            <div
              class="create-token-popup__key"
              @click="copy(secretKey)"
            >
              {{ secretKey }}
            </div>
          </div>
        </template>
        <template v-else>
          <UiInput
            v-model="tokenName"
            :placeholder="$t('settings.tokens.enter_token_name')"
            :label="$t('settings.tokens.token_name')"
            size="big"
            :error="errors.name"
            autosize
            class="offer-input offer-input-name"
          />
          <UiRadioGroup
            v-model="service"
            :label="$t('settings.tokens.service')"
            :options="serviceOptions"
          />
        </template>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <template v-if="completeState">
          <UiButton
            filled
            size="big"
            icon="check"
            @click="handleClose"
          >
            {{ $t('settings.tokens.complete') }}
          </UiButton>
        </template>
        <template v-else>
          <UiButton
            type="negative"
            size="big"
            icon="times"
            @click="handleClose"
          >
            {{ $t('crm.buttons.cancel') }}
          </UiButton>
          <UiButton
            filled
            size="big"
            icon="check"
            @click="handleSave"
          >
            {{ $t('settings.tokens.create_token') }}
          </UiButton>
        </template>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.create-token-popup {
  &__key {
    color: var(--text-link);
    cursor: pointer;
    font-size: 14px;
    padding: 8px;
  }
}
</style>







